import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Chapter, SectionTitle, Reveal } from "./Reveal";
import { GALLERY } from "@/data/content";

const Gallery = () => {
  const [selected, setSelected] = useState(null);

  const move = (dir) =>
    setSelected((s) => (s + dir + GALLERY.length) % GALLERY.length);

  return (
    <section id="galleria" data-testid="gallery-section" className="relative py-24 sm:py-36 bg-[#141210]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Chapter number="07" title="La Galleria" />
        <SectionTitle>Piatti, sala, momenti.</SectionTitle>

        <div
          data-testid="gallery-grid"
          className="mt-14 columns-2 md:columns-3 lg:columns-4 gap-4 [column-fill:_balance]"
        >
          {GALLERY.map((g, i) => (
            <Reveal key={g.src} delay={(i % 4) * 0.07} className="mb-4 break-inside-avoid">
              <button
                data-testid={`gallery-item-${i}`}
                onClick={() => setSelected(i)}
                className="group relative block w-full overflow-hidden text-left"
              >
                <img
                  src={g.src}
                  alt={`${g.caption} — John Tartarino, Follonica`}
                  loading="lazy"
                  className="w-full object-cover group-hover:scale-110 transition-transform duration-[1.2s] ease-out"
                />
                <span className="absolute inset-0 bg-gradient-to-t from-[#0D0C0B]/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-3 left-3 text-[10px] uppercase tracking-[0.3em] text-[#C5A059] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                  {g.caption}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="lightbox-modal"
            className="fixed inset-0 z-[60] bg-[#0D0C0B]/95 backdrop-blur-md flex items-center justify-center p-4 sm:p-10"
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setSelected(null)}
              className="absolute top-5 right-5 text-[#F7F4EE] hover:text-[#C5A059] transition-colors p-2"
              aria-label="Chiudi"
            >
              <X size={28} strokeWidth={1.5} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); move(-1); }}
              className="absolute left-3 sm:left-8 text-[#F7F4EE] hover:text-[#C5A059] transition-colors p-2"
              aria-label="Precedente"
            >
              <ChevronLeft size={36} strokeWidth={1.5} />
            </button>
            <motion.figure
              key={selected}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={GALLERY[selected].src.replace("w=1000", "w=1600")}
                alt={GALLERY[selected].caption}
                className="w-full max-h-[78vh] object-contain"
              />
              <figcaption
                data-testid="lightbox-caption"
                className="mt-4 text-center text-xs uppercase tracking-[0.35em] text-[#C5A059]"
              >
                {GALLERY[selected].caption}
              </figcaption>
            </motion.figure>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); move(1); }}
              className="absolute right-3 sm:right-8 text-[#F7F4EE] hover:text-[#C5A059] transition-colors p-2"
              aria-label="Successiva"
            >
              <ChevronRight size={36} strokeWidth={1.5} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
